import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../services/api';
import './Auth.css';

export const LoginForm = () => {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const { login } = useAuth();

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const resetLoginForm = () => {
    setFormData(prev => ({
      ...prev,
      email: '',
      password: ''
    }));
    setSuccess('');
  };

  const switchTab = (tab: 'login' | 'register') => {
    setActiveTab(tab);
    resetForm();
    setRequires2FA(false);
    setUserId(null);
    setTwoFactorCode('');
  };

  // Vollständig aus 2FA zurück zum Login
  const exitFrom2FA = () => {
    setRequires2FA(false);
    setUserId(null);
    setTwoFactorCode('');
    setError('');
    // Login-Daten NICHT zurücksetzen, damit User nicht neu eingeben muss
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const loginData = {
        email: formData.email.trim(),
        password: formData.password
      };

      const result = await login(loginData);
      
      if (result && result.requires2FA) {
        setRequires2FA(true);
        setUserId(result.userId);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Login fehlgeschlagen';
      
      setError(errorMessage);
      
      // NUR bei bestimmten Fehlern Login-Daten zurücksetzen
      if (errorMessage.includes('nicht aktiviert') || errorMessage.includes('deaktiviert')) {
        resetLoginForm();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validierung
    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      setLoading(false);
      return;
    }

    try {
      const registerData = {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      };

      await authAPI.register(registerData);
      setSuccess('Registrierung erfolgreich! Sie können sich nach einer Freischaltung durch einen Administrator anmelden.');
      
      // Nach erfolgreicher Registrierung zum Login wechseln
      setTimeout(() => {
        switchTab('login');
        setFormData(prev => ({
          ...prev,
          firstName: '',
          lastName: '',
          confirmPassword: ''
        }));
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const verifyData = {
        userId: userId!,
        token: twoFactorCode.trim()
      };

      await login(verifyData, true);
    } catch (err: any) {
      setError(err.response?.data?.error || '2FA-Verifizierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Ansicht
  if (requires2FA) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>🔐 Dialga TMS</h1>
            <p>Zwei-Faktor-Authentifizierung</p>
          </div>

          <div className="auth-content">
            <form className="auth-form" onSubmit={handle2FAVerification}>
              <h2>2FA-Code eingeben</h2>
              <p>Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.</p>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="twoFactorCode">2FA-Code</label>
                <input
                  id="twoFactorCode"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="12345678"
                  maxLength={8}
                  className="form-input verification-input"
                  autoComplete="one-time-code"
                  required
                />
              </div>

              <button 
                type="submit" 
                className="auth-button primary"
                disabled={loading || twoFactorCode.length !== 8 && twoFactorCode.length !== 6}
              >
                {loading ? 'Verifizieren...' : 'Verifizieren'}
              </button>

              <button 
                type="button" 
                className="back-btn"
                onClick={exitFrom2FA}
                disabled={loading}
              >
                ← Zurück zum Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1><img src="/chronios.svg" alt="Chronios" style={{ width: '170px', height: 'auto' }} /></h1>
          <p>Time Management System</p>
        </div>

        {/* Tab Navigation */}
        <div className="auth-tabs">
          <button 
            type="button"
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => switchTab('login')}
          >
            Anmelden
          </button>
          <button 
            type="button"
            className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => switchTab('register')}
          >
            Registrieren
          </button>
        </div>

        <div className="auth-content">
          {activeTab === 'login' ? (
            <form className="auth-form" onSubmit={handleLogin}>
              <h2>Willkommen zurück</h2>
              <p>Melden Sie sich in Ihrem Konto an</p>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">E-Mail-Adresse</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                  placeholder="ihre.email@example.com"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Passwort</label>
                <input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                  placeholder="••••••••"
                  className="form-input"
                  required
                />
              </div>

              <button 
                type="submit" 
                className="auth-button primary"
                disabled={loading}
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleRegister} autoComplete="off">
              {/* Dummy-Felder um Browser-Autocomplete zu umgehen */}
              <input type="text" style={{display: 'none'}} autoComplete="off" />
              <input type="password" style={{display: 'none'}} autoComplete="off" />
              
              <h2>Konto erstellen</h2>
              <p>Registrieren Sie sich für ein neues Konto</p>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {success && (
                <div className="success-message">
                  {success}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Vorname</label>
                  <input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))}
                    placeholder="Max"
                    className="form-input"
                    required
                    autoComplete="off"
                    data-form-type="other"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Nachname</label>
                  <input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))}
                    placeholder="Mustermann"
                    className="form-input"
                    required
                    autoComplete="off"
                    data-form-type="other"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="registerEmail">E-Mail-Adresse</label>
                <input
                  id="registerEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                  placeholder="ihre.email@example.com"
                  className="form-input"
                  required
                  autoComplete="off"
                  data-form-type="other"
                />
              </div>

              <div className="form-group">
                <label htmlFor="registerPassword">Passwort</label>
                <input
                  id="registerPassword"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                  placeholder="••••••••"
                  className="form-input"
                  minLength={6}
                  required
                  autoComplete="new-password"
                  data-form-type="other"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Passwort bestätigen</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({...prev, confirmPassword: e.target.value}))}
                  placeholder="••••••••"
                  className="form-input"
                  required
                  autoComplete="new-password"
                  data-form-type="other"
                  />
              </div>

              <button 
                type="submit" 
                className="auth-button primary"
                disabled={loading}
              >
                {loading ? 'Registrieren...' : 'Registrieren'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;